import { Icon, usePopover } from '@aibees/shared/components';
import { classnames } from '@aibees/shared/utils';
import { Tooltip, Typography } from '@mui/material';
import React, { memo, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Styled from './styles';
import * as SX from './sx';
import type { SidebarSingleItemProps as Props } from './types';
import { AppBadgeName } from '../app-badge-name';

export const SidebarSingleItem = memo(
  ({
    className,
    open,
    title,
    icon,
    route,
    homePath,
    alwaysDeactive,
    hideBadgeName,
    sx,
    comingSoon,
    actionMode = 'route',
  }: Props) => {
    const location = useLocation();
    const navigate = useNavigate();
    const state = usePopover();

    const active = useMemo(
      () =>
        !alwaysDeactive && route
          ? location.pathname.includes(homePath ?? route)
          : false,
      [alwaysDeactive, route, homePath, location.pathname]
    );

    const iconColor = useMemo(() => {
      let color = 'grey.600';
      if (active) color = 'primary';
      if (comingSoon) color = 'grey.400';
      return color;
    }, [active, comingSoon]);

    const handleClick = useCallback(
      (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (comingSoon) {
          e.preventDefault();
          return;
        }
        if (actionMode === 'route') {
          navigate(route ?? '#');
          e.preventDefault();
        }
        if (actionMode === 'popup') {
          state.onOpen(e.currentTarget as HTMLElement);
        }
      },
      [comingSoon, actionMode, route, navigate, state]
    );

    return (
      <Styled.Wrapper
        className={classnames('SidebarSingleItem', className, { open })}
        sx={sx}
      >
        {!open ? (
          <>
            <Tooltip title={title} placement="right" enterDelay={500}>
              <Styled.Button
                className="SidebarSingleItemButton"
                disabled={comingSoon}
                variant="outlined"
                size="small"
                color={active || !!state.open ? 'primary' : 'default'}
                onClick={handleClick}
              >
                <Icon name={icon} color={iconColor} />
              </Styled.Button>
            </Tooltip>
            {!hideBadgeName && (
              <AppBadgeName
                appName={`${title} ${comingSoon ? ' (Coming Soon)' : ''}`}
              />
            )}
          </>
        ) : (
          <Styled.SettingWrapper
            to={route ?? '#'}
            className={classnames({ active })}
          >
            <Icon
              name={icon}
              size={20}
              color={active ? 'primary[500]' : 'common.black'}
            />
            <Typography
              variant="body4"
              color={active ? 'primary[500]' : 'common.black'}
              children={title}
              sx={SX.title}
            />
          </Styled.SettingWrapper>
        )}
      </Styled.Wrapper>
    );
  }
);

SidebarSingleItem.displayName = 'SidebarSingleItem';
